import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/Button';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/**
 * Not Found (404) Page Component
 * 
 * Displays a friendly 404 error page with navigation options.
 */
export function NotFound() {
  useDocumentTitle('Page Not Found | SupportDesk');
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-between">
      {/* Skip Link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-white focus:text-primary-700 focus:font-medium focus:shadow-md focus:rounded-md focus:ring-2 focus:ring-primary-500 focus:outline-none"
      >
        Skip to main content
      </a>

      {/* Header Landmark */}
      <header className="py-4 px-4 sm:px-6 lg:px-8 flex-shrink-0" role="banner">
        <div className="max-w-7xl mx-auto flex items-center space-x-3">
          <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 2L2 7v10c0 5.55 3.84 9.74 9 11 5.16-1.26 9-5.45 9-11V7l-10-5z"/>
            </svg>
          </div>
          <span className="text-2xl font-bold text-gray-900 font-heading">
            SupportDesk
          </span>
        </div>
      </header>

      {/* Main Content Landmark */}
      <main id="main-content" className="flex-1 flex flex-col justify-center py-6 sm:px-6 lg:px-8" role="main">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            {/* 404 Illustration */}
            <div className="mx-auto h-24 w-24 text-primary-500 mb-6">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.346 0-4.518.856-6.193 2.291A9.965 9.965 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10a9.965 9.965 0 01-3.807 7.291z" />
              </svg>
            </div>

            <h1 className="text-5xl font-bold text-gray-900 mb-2">404</h1>
            <h2 className="text-xl font-semibold text-gray-700 mb-3">Page Not Found</h2>
            <p className="text-gray-500 mb-6 max-w-md mx-auto text-sm">
              Sorry, we couldn't find the page you're looking for. The page might have been moved, deleted, or you might have typed the URL incorrectly.
            </p>

            <div className="space-y-3">
              {isAuthenticated ? (
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/dashboard">
                    <Button variant="primary" fullWidth>
                      Go to Dashboard
                    </Button>
                  </Link>
                  <Link to="/tickets">
                    <Button variant="outline" fullWidth>
                      View Tickets
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/login">
                    <Button variant="primary" fullWidth>
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button variant="outline" fullWidth>
                      Create Account
                    </Button>
                  </Link>
                </div>
              )}
            </div>

            {/* Additional Help */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-left">
              <h3 className="text-base font-medium text-gray-900 mb-3 text-center">Need Help?</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-medium text-gray-700 mb-1">Common Pages</h4>
                  <ul className="space-y-1 text-gray-500">
                    {isAuthenticated ? (
                      <>
                        <li><Link to="/dashboard" className="hover:text-primary-600 transition-colors">Dashboard</Link></li>
                        <li><Link to="/tickets" className="hover:text-primary-600 transition-colors">Tickets</Link></li>
                        <li><Link to="/profile" className="hover:text-primary-600 transition-colors">Profile</Link></li>
                      </>
                    ) : (
                      <>
                        <li><Link to="/login" className="hover:text-primary-600 transition-colors">Sign In</Link></li>
                        <li><Link to="/register" className="hover:text-primary-600 transition-colors">Create Account</Link></li>
                      </>
                    )}
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-gray-700 mb-1">Get Support</h4>
                  <ul className="space-y-1 text-gray-500">
                    {isAuthenticated ? (
                      <>
                        <li><Link to="/tickets/new" className="hover:text-primary-600 transition-colors">Create Support Ticket</Link></li>
                        <li><Link to="/tickets" className="hover:text-primary-600 transition-colors">Track Existing Tickets</Link></li>
                      </>
                    ) : (
                      <li>
                        <span className="text-gray-500 text-xs">Contact your support administrator for assistance</span>
                      </li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer Landmark */}
      <footer className="py-4 text-center text-xs text-gray-400 flex-shrink-0" role="contentinfo">
        <p>© {new Date().getFullYear()} SupportDesk MVP - Professional Support Desk Solution</p>
      </footer>
    </div>
  );
}